import { useState } from 'react';

import { hasAddedLines } from '@/utils/checkout';

import { useCheckout } from '@/hooks/useCheckout';
import { useRouter } from '@/hooks/useRouter';
import { useSendAddedToCartAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import {
  generateEyewearDetailPagePath,
  generateStrapDetailPagePath,
  generateWatchDetailPagePath,
  isEyewearHit,
  isStrapHit,
  isWatchHit,
} from '@/integrations/algolia/helpers';
import type { ProductHit, WatchHit } from '@/integrations/algolia/types';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

export const useProductListCardCommon = (hit: ProductHit) => {
  const { push } = useRouter();
  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const [isLocalProcessing, setIsLocalProcessing] = useState(false);

  const sendAddedToCartAlgoliaEvent = useSendAddedToCartAlgoliaEvent();

  const {
    endProcessing,
    lineAdd,
    snapshot: { isProcessing },
    startProcessing,
  } = useCheckout();

  const {
    locale,
    productCatalogMode: contextProductCatalogMode,
    query,
  } = useRegionProvider();
  const name = (
    hit.name[locale?.code?.toUpperCase() || 'EN_GB'] ||
    hit.name['EN_GB'] ||
    hit.name['EN_US']
  ).replace(/\u00A0/g, ' ');

  const detailPath = generateDetailPagePath(hit);

  const sendAddedToCartEvent = () => {
    const queryID =
      '__queryID' in hit && typeof hit.__queryID === 'string'
        ? hit.__queryID
        : undefined;
    sendAddedToCartAlgoliaEvent(hit.objectID, hit.price, hit.currency, queryID);
  };
  const imageUrl = (hit as WatchHit)?.image_url ?? hit?.thumbnail_url;

  const addToBag = async (
    customOrderFun?: () => Promise<object[] | undefined>,
  ) => {
    if (customOrderFun) {
      const addedLines = await customOrderFun();
      if (hasAddedLines(addedLines)) {
        sendAddedToCartEvent();
      }
      return;
    }
    setIsLocalProcessing(true);
    try {
      const addedLines = await lineAdd(hit?.variant_id, hit?.call_to_action);
      if (hasAddedLines(addedLines)) {
        sendAddedToCartEvent();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLocalProcessing(false);
    }
  };

  const handleClickEvent = async (
    e: React.MouseEvent,
    customOrderFun?: () => Promise<object[] | undefined>,
    productCatalogMode = contextProductCatalogMode,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (productCatalogMode) {
      return push(detailPath);
    } else if (
      hit.call_to_action === 'ORDER' ||
      hit.call_to_action === 'PRE_ORDER'
    ) {
      await addToBag(customOrderFun);
    } else if (hit.call_to_action === 'NOTIFY') {
      if (hit.objectID) {
        openNotifyMe({ sku: hit.objectID });
      }
    } else if (hit.call_to_action === 'NO_CTA') {
      setContactUsModalStatus(true, hit.objectID);
    }
  };
  return {
    locale,
    query,
    detailPath,
    openNotifyMe,
    setContactUsModalStatus,
    isLocalProcessing,
    setIsLocalProcessing,
    sendAddedToCartEvent,
    name,
    contextProductCatalogMode,
    imageUrl,
    handleClickEvent,
    endProcessing,
    lineAdd,
    isProcessing,
    startProcessing,
  };
};

export const generateDetailPagePath = (hit: ProductHit) => {
  if (isWatchHit(hit)) {
    return generateWatchDetailPagePath(hit);
  } else if (isStrapHit(hit)) {
    return generateStrapDetailPagePath(hit);
  } else if (isEyewearHit(hit)) {
    return generateEyewearDetailPagePath(hit);
  } else {
    return '#';
  }
};
